'use client'

import { useQuery } from '@tanstack/react-query'
import { useApiFetch } from '@/features/auth'
import {
  fetchCallStatus,
  fetchCaseDetail,
  fetchCaseDocumentList,
  fetchCaseEvents,
  fetchCaseGoals,
  fetchCaseHoldings,
  fetchCasePreview,
  fetchCasePrescriptionSteps,
  fetchCases,
  fetchCaseSnapshots,
  fetchCalls,
  fetchCaseTrades,
  fetchCaseUploadLinks,
  type CallsListParams,
  type CasesListParams,
} from '@/features/cases/api/cases.api'
import { queryKeys } from '@/lib/queryKeys'

/**
 * Real `GET /cases` — staff/admin case-list screens only. The mock `useAllCases()` this replaced
 * was deleted in Phase 12 once its last consumer was cut over.
 */
export function useCases(params: CasesListParams) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.cases.list(params),
    queryFn: () => fetchCases(authedFetch, params),
  })
}

export function useCaseDetail(caseId: string | undefined) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.cases.real(caseId ?? ''),
    queryFn: () => fetchCaseDetail(authedFetch, caseId!),
    enabled: !!caseId,
  })
}

/** Real `GET /cases/:id/holdings` — distinct from the mock `useHoldings()` (investor dashboard, Phase 9). */
export function useCaseHoldings(caseId: string | undefined) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.cases.holdings(caseId ?? ''),
    queryFn: () => fetchCaseHoldings(authedFetch, caseId!),
    enabled: !!caseId,
  })
}

/** Real `GET /cases/:id/trades` (paginated) — distinct from the mock `useTrades()` (investor dashboard, Phase 9). */
export function useCaseTrades(caseId: string | undefined, page: number, pageSize: number) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.cases.trades(caseId ?? '', page, pageSize),
    queryFn: () => fetchCaseTrades(authedFetch, caseId!, { page, pageSize }),
    enabled: !!caseId,
  })
}

/** Real `GET /cases/:id/goals` — Build Portfolio only, returns `[]` for the other 2 service types.
 * Fetched unconditionally alongside holdings/trades, same as those, rather than gated on
 * `serviceType` — matches this file's existing "let the backend return the empty shape" style. */
export function useCaseGoals(caseId: string | undefined) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.cases.goals(caseId ?? ''),
    queryFn: () => fetchCaseGoals(authedFetch, caseId!),
    enabled: !!caseId,
  })
}

/** Real `GET /cases/:id/prescription-steps` — distinct from the mock `usePrescriptionSteps()` (also used by the deferred investor dashboard). */
export function useCasePrescriptionSteps(caseId: string | undefined) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.cases.prescriptionSteps(caseId ?? ''),
    queryFn: () => fetchCasePrescriptionSteps(authedFetch, caseId!),
    enabled: !!caseId,
  })
}

/** Real `GET /cases/:id/documents` — distinct from the mock `useCaseDocuments()` (also shared
 * with the deferred investor-dashboard's `CaseDocuments` component). */
export function useCaseDocumentList(caseId: string | undefined) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.cases.documents(caseId ?? ''),
    queryFn: () => fetchCaseDocumentList(authedFetch, caseId!),
    enabled: !!caseId,
  })
}

/** Real `GET /cases/:id/upload-links` — fully staff-only, no mock counterpart exists (the
 * investor dashboard has no concept of "links I generated for myself"). */
export function useCaseUploadLinks(caseId: string | undefined) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.cases.uploadLinks(caseId ?? ''),
    queryFn: () => fetchCaseUploadLinks(authedFetch, caseId!),
    enabled: !!caseId,
  })
}

/** Real `GET /calls` — the mock `useCallLogs()`/`useAllCallLogs()`/`useCallLogsByInvestor()`
 * (`use-staff-cases.ts`) were deleted in Phase 12 once `staff-clients-view.tsx` (their last real
 * consumer) cut over to this hook. Staff/admin only; staff are implicitly scoped to their own
 * calls server-side. `enabled` lets a per-case caller (e.g. `client-detail.tsx`, scoping via
 * `search: caseDetail.reference`) defer the fetch until its scoping value actually exists — the
 * two list-screen callers never need it and default to on. */
export function useCalls(params: CallsListParams, options: { enabled?: boolean } = {}) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.calls.list(params),
    queryFn: () => fetchCalls(authedFetch, params),
    enabled: options.enabled ?? true,
  })
}

/** One of two narrowly-scoped exceptions to this app's "no polling" convention (the other is
 * `useMarketTicker`, `features/market-data`) — a live call's status genuinely needs near-real-time
 * feedback for the ~10-30s a call takes to connect, and there is no WebSocket infra to push it
 * instead. Stops polling (returns `false`, not just a slower interval) once `status` reaches a
 * terminal value. */
export function useCallStatus(callId: string | undefined, options: { enabled?: boolean } = {}) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.calls.status(callId ?? ''),
    queryFn: () => fetchCallStatus(authedFetch, callId as string),
    enabled: (options.enabled ?? true) && !!callId,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'completed' || status === 'failed' ? false : 2000
    },
  })
}

/** No current UI consumer — built to match the 9-endpoint scope, available for whenever a
 * portfolio-value-over-time chart is added. */
export function useCaseSnapshots(caseId: string | undefined) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.cases.snapshots(caseId ?? ''),
    queryFn: () => fetchCaseSnapshots(authedFetch, caseId!),
    enabled: !!caseId,
  })
}

/** No current UI consumer — built to match the 9-endpoint scope, available for whenever a
 * case activity/timeline view is added. */
export function useCaseEvents(caseId: string | undefined, page: number, pageSize: number) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.cases.events(caseId ?? '', page, pageSize),
    queryFn: () => fetchCaseEvents(authedFetch, caseId!, { page, pageSize }),
    enabled: !!caseId,
  })
}

/** No current UI consumer — the "Preview client dashboard" button stays disabled until Phase 9
 * builds the real investor-dashboard components this would render into. */
export function useCasePreview(caseId: string | undefined) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.cases.preview(caseId ?? ''),
    queryFn: () => fetchCasePreview(authedFetch, caseId!),
    enabled: !!caseId,
  })
}
